import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { toast } from 'react-toastify';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import 'react-toastify/dist/ReactToastify.css';

const LocationInput = ({ 
  value, 
  onChange, 
  placeholder = 'Enter location',
  label,
  className = '',
  iconClassName = '',
  onPlaceSelected = null,
  country = 'in', // Default to India
  types = ['(cities)'],
  required = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const autocompleteRef = useRef(null);
  const autocompleteServiceRef = useRef(null);

  const { isLoaded, loadError, autocompleteService } = useGoogleMaps();

  useEffect(() => {
    if (loadError) {
      console.error('Error with Google Maps API:', loadError);
      toast.error('Error loading maps. Please try refreshing the page.');
    }
  }, [loadError]);

  // Initialize autocomplete service when Google Maps is loaded
  useEffect(() => {
    if (isLoaded && autocompleteService) {
      autocompleteServiceRef.current = autocompleteService;
    }
  }, [isLoaded, autocompleteService]);

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
    // Set component restrictions for India only
    if (autocomplete.setComponentRestrictions) {
      autocomplete.setComponentRestrictions({ country: ['in'] });
    }
    // Set types to filter by cities
    if (autocomplete.setTypes) {
      autocomplete.setTypes(['(cities)']);
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && (place.formatted_address || place.name)) {
        const displayName = place.formatted_address || place.name;
        onChange(displayName);
        setShowSuggestions(false);
        if (onPlaceSelected) {
          onPlaceSelected(place);
        }
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(true);
  };

  const clearInput = () => {
    onChange('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  if (!isLoaded) {
    return (
      <div className={`relative ${className}`}>
        {label && <label className="block text-gray-700 text-sm font-medium mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
        <div className="relative">
          <FaMapMarkerAlt className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 ${iconClassName}`} />
          <input 
            type="text" 
            value={value}
            onChange={handleInputChange}
            className="w-full border border-gray-300 bg-gray-100 rounded-xl pl-10 pr-8 py-2.5 text-sm sm:text-base shadow-sm cursor-not-allowed"
            placeholder="Loading maps..."
            disabled
            required={required}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-gray-700 text-sm font-medium mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <FaMapMarkerAlt className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 ${iconClassName}`} />
        <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onFocus={() => setShowSuggestions(true)}
            className={`w-full border border-gray-300 rounded-xl pl-10 pr-8 py-2.5 text-sm sm:text-base shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition duration-200 ${value ? 'bg-white' : 'bg-gray-50'}`}
            placeholder={placeholder}
            required={required}
            autoComplete="off"
            spellCheck="false"
          />
        </Autocomplete>
        {value && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Clear input"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LocationInput;
